import { useEffect, useMemo } from 'react';

import { FormikProps } from 'formik';

import {
  adminPharmaciesInfo,
  adminResetValue,
  getPharmaciesInfo,
  HelpTypes,
  searchedPharmacies,
} from '@pharmaplan/common';

import { toPharmacyUrl } from '../../../components/Admin/BroadcastList/CreateEditBroadcastNotification/BroadcastFields/BroadcastNavigationFields/helper';
import { toOptions } from '../../../components/Admin/ExpressBooking/ExpressBooking';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';
import useAutoComplete from '../../useAutocomplete';

interface IPharmacyOption {
  label: string;
  subtitle: string;
  id: string;
}

const pharmacyUserTypes = [HelpTypes.pharmacy];
const pharmaciesInfoKey = 'pharmaciesInfo';
const pharmacyUrlKey = 'pharmacyUrl';
const pharmacySelectedKey = 'pharmacySelected';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useBroadcastPharmacyNavigation = (formik: FormikProps<any>) => {
  const dispatch = useAppDispatch();

  const {
    pharmacies: pharmacyQuery,
    pharmacyId,
    pharmacySelected,
  } = formik.values;

  const pharmacies = useAppSelector(searchedPharmacies);
  const { name: loadedPharmacyName } = useAppSelector(adminPharmaciesInfo);

  const pharmacyUrl = toPharmacyUrl(pharmacyId);

  // A RESENT NOTIFICATION ONLY CARRIES THE ID, SO ITS NAME IS FETCHED ONCE
  const isNameMissing = !!pharmacyId && !pharmacySelected;

  const { debouncedInput: pharmacyInput } = useAutoComplete({
    userTypes: pharmacyUserTypes,
  });

  // KEEPS THE PRESELECTED PHARMACY MATCHABLE AGAINST THE SEARCH RESULTS
  const pharmacyOptions = useMemo(() => {
    const searchOptions = toOptions(pharmacies);
    const selected = pharmacySelected as IPharmacyOption | null;

    if (!selected || searchOptions.some(({ id }) =>
      id === selected.id)) {
      return searchOptions;
    }

    return [selected, ...searchOptions];
  }, [pharmacies, pharmacySelected]);

  useEffect(() => {
    pharmacyInput(pharmacyQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyQuery]);

  useEffect(() => {
    if (!isNameMissing) {
      return;
    }

    dispatch(adminResetValue([pharmaciesInfoKey]));
    dispatch(getPharmaciesInfo({ id: pharmacyId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyId, isNameMissing]);

  useEffect(() => {
    if (!isNameMissing || !loadedPharmacyName) {
      return;
    }

    formik.setFieldValue(pharmacySelectedKey, {
      label: loadedPharmacyName,
      subtitle: '',
      id: pharmacyId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedPharmacyName, isNameMissing]);

  useEffect(() => {
    formik.setFieldValue(pharmacyUrlKey, pharmacyUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyUrl]);

  useEffect(
    () =>
      () => {
        dispatch(adminResetValue([pharmaciesInfoKey]));
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { pharmacyOptions };
};

export default useBroadcastPharmacyNavigation;
